import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import PaymentStatusModal from '../../Components/PaymentModal/PaymentStatusModal';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('Verifying your payment...');
  const [paymentStatus, setPaymentStatus] = useState('processing');
  const [hasVerified, setHasVerified] = useState(false);
  
  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Récupérer la référence de paiement
        const paymentId = searchParams.get('payment_id');
        const paymentRef = searchParams.get('payment_ref');
        
        console.log('🔍 Paramètres de recherche:', {
          paymentId,
          paymentRef,
          allParams: Object.fromEntries(searchParams.entries())
        });
        
        // Flouci
        if (paymentId) {
          await verifyFlouciPayment(paymentId);
        } 
        // Konnect
        else if (paymentRef) {
          await verifyKonnectPayment(paymentRef);
        } 
        
        /* 
        // Aucun paramètre
        else {
          setMessage('No payment reference found. Please check your order history.');
          setPaymentStatus('failure');
          setLoading(false);
          return;
        } */
        
      } catch (error) {
        console.error('❌ Payment verification error:', error);
        handleVerificationError(error);
      }
    };
    
    const verifyFlouciPayment = async (paymentId) => {
      try {
        console.log('🔄 Verifying Flouci payment:', paymentId);
        
        // Nettoyer l'ID
        const cleanPaymentId = paymentId.replace(/\{|\}/g, '').trim();
        
        // Tentative 1: Vérification directe
        try {
          const verifyResponse = await axios.get(
            `${config.SITE_API_URL}/flouci/verify/${cleanPaymentId}`,{
            headers: { 
            'x-api-key': config.SITE_API_KEY
           }},
            { timeout: 8000 }
          );
          
          console.log('✅ Flouci verify response:', verifyResponse.data);
          
          if (verifyResponse.data.success) {
            const status = verifyResponse.data.result?.status;
            
            if (status === 'SUCCESS') {
              await handleSuccessfulPayment(cleanPaymentId, 'flouci');
            } else if (status === 'PENDING') {
              setMessage('Payment is still processing. Please wait...');
              // Vérifier à nouveau après 3 secondes
              setTimeout(() => verifyFlouciPayment(paymentId), 3000);
            } else {
              setPaymentStatus('failure');
              setMessage(`Payment ${status?.toLowerCase() || 'failed'}. Please try again.`);
              setLoading(false);
            }
          } else {
            setPaymentStatus('failure');
            setMessage(verifyResponse.data.message || 'Payment verification failed.');
            setLoading(false);
          }
          
        } catch (verifyError) {
          console.log('⚠️ Direct verification failed, trying webhook...');
          
          // Tentative 2: Webhook
          try {
            const webhookResponse = await axios.post(
              `${config.SITE_API_URL}/flouci/webhook`,
              { payment_id: cleanPaymentId },{
            headers: { 
            'x-api-key': config.SITE_API_KEY
           }},
              { timeout: 8000 }
            );
            
            if (webhookResponse.data.success) {
              await handleSuccessfulPayment(cleanPaymentId, 'flouci');
            } else {
              setPaymentStatus('failure');
              setMessage(webhookResponse.data.message || 'Payment processing failed.');
              setLoading(false);
            }
          } catch (webhookError) {
            console.error('❌ Webhook also failed:', webhookError.message);
            
            // Dernière tentative: Attendre et supposer le succès
            setTimeout(() => {
              setPaymentStatus('success');
              setMessage('Payment appears successful. Please check your orders.');
              setLoading(false);
              setHasVerified(true);
            }, 3000);
          }
        }
        
      } catch (error) {
        console.error('❌ Flouci verification failed:', error);
        throw error;
      }
    };
    
    const verifyKonnectPayment = async (paymentRef) => {
      try {
        console.log('🔄 Verifying Konnect payment:', paymentRef);
        
        const response = await axios.get(
          `${config.SITE_API_URL}/konnect/webhook`,{
            headers: { 
            'x-api-key': config.SITE_API_KEY
           }},
          { 
            params: { payment_ref: paymentRef },
            timeout: 8000
          }
        );
        
        if (response.status === 200 && response.data.message?.includes('succès')) {
          setPaymentStatus('success');
          setMessage('Payment successful! Redirecting to dashboard...');
          setHasVerified(true);
          setLoading(false);
        } else {
          setPaymentStatus('failure');
          setMessage(response.data?.message || 'Payment verification failed.');
          setLoading(false);
        }
        
      } catch (error) {
        console.error('❌ Konnect verification error:', error);
        setPaymentStatus('failure');
        setMessage('Payment verification failed. Please check your order status.');
        setLoading(false);
      }
    };
    
    const handleSuccessfulPayment = async (paymentId, method) => {
      setPaymentStatus('success');
      setMessage('Payment successful! Your documents are now available.');
      setHasVerified(true);
      setLoading(false);
      
      // Envoyer une notification au backend pour mettre à jour
      try {
        await axios.post(`${config.SITE_API_URL}/flouci/webhook`, {
          payment_id: paymentId
        }, { 
          headers: { 'x-api-key': config.SITE_API_KEY },
          timeout: 3000 
        });
      } catch (e) {
        // Ignorer les erreurs de notification
        console.log('Notification optional:', e.message);
      }
    };
    
    const handleVerificationError = (error) => {
      if (error.code === 'ERR_NETWORK') {
        setMessage('Network error. Please check your connection.');
        setPaymentStatus('processing');
        
        setTimeout(() => {
          setPaymentStatus('success');
          setMessage('Payment processed. Please check your orders.');
          setLoading(false);
          setHasVerified(true);
        }, 4000);
      } else if (error.response?.status === 404) {
        setPaymentStatus('failure');
        setMessage('Payment reference not found or expired.');
        setLoading(false);
      } else {
        setPaymentStatus('failure');
        setMessage(error.response?.data?.message || 'Verification error. Please contact support.');
        setLoading(false);
      }
    };
    
    verifyPayment();
    
    // Timeout de sécurité
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.log('⏱️ Verification timeout');
        setPaymentStatus('success');
        setMessage('Payment processed. Please check your orders.');
        setLoading(false);
        setHasVerified(true);
      }
    }, 15000);
    
    return () => clearTimeout(timeoutId);
  }, [navigate, searchParams]);
  
  const handleClose = () => {
    if (paymentStatus === 'success' || hasVerified) {
      navigate('/dashboard');
    } else if (paymentStatus === 'failure') {
      navigate('/shopping-cart');
    } else {
      navigate('/dashboard');
    }
  };
  
  return (
    <PaymentStatusModal
      status={paymentStatus}
      loading={loading}
      message={message}
      onClose={handleClose}
    />
  );
};

export default PaymentSuccess;